import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { X, Building2, Send, Calendar, CheckCircle2, MapPin, Bed, Users, Loader2 } from "lucide-react";

/**
 * Vitrine da Clínica (só consultor RAF vê).
 * Mostra fotos, planos, room_photos, profissionais e dados de contato.
 * Rodapé com 3 CTAs: Compartilhar anônimo · Agendar · Reservar.
 */
export const ClinicVitrineModal = ({ clinicId, caseData, onClose, onShared, onScheduled, onReserve }) => {
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShare, setShowShare] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);

  useEffect(() => {
    if (!clinicId) return;
    setLoading(true);
    api.get(`/consultant/clinic/${clinicId}/full`)
      .then(({ data }) => setClinic(data))
      .catch(() => toast.error("Erro ao carregar clínica"))
      .finally(() => setLoading(false));
  }, [clinicId]);

  if (!clinicId) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="clinic-vitrine-modal">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between shrink-0">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
              Vitrine · confidencial (só consultor)
            </div>
            <h2 className="font-display font-bold text-xl text-[#0B2B62] truncate flex items-center gap-2">
              <Building2 className="w-5 h-5 shrink-0" />
              {clinic?.name || "Carregando…"}
              {clinic?.code && <span className="text-xs px-2 py-0.5 bg-slate-100 rounded-full font-mono text-slate-600">{clinic.code}</span>}
            </h2>
            {clinic && (
              <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3">
                <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{clinic.city}, {clinic.state}</span>
                <span className="inline-flex items-center gap-1"><Bed className="w-3 h-3" />{clinic.beds_available} vaga(s)</span>
                {clinic.plans?.length > 0 && <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{clinic.plans.length} plano(s)</span>}
              </div>
            )}
          </div>
          <button onClick={onClose} data-testid="vitrine-close" className="p-2 rounded-md hover:bg-slate-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : clinic && (
            <>
              {clinic.hero_image && (
                <img src={clinic.hero_image} alt="" className="w-full h-52 object-cover rounded-lg" />
              )}
              {clinic.description && (
                <section>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">Sobre a unidade</div>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{clinic.description}</p>
                </section>
              )}

              {(clinic.gallery || []).length > 0 && (
                <section>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Galeria da unidade ({clinic.gallery.length})</div>
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                    {clinic.gallery.map((url, i) => (
                      <img key={i} src={url} alt="" className="aspect-square object-cover rounded-md" data-testid={`vitrine-gallery-${i}`} />
                    ))}
                  </div>
                </section>
              )}

              {(clinic.plans || []).length > 0 && (
                <section>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Planos disponíveis</div>
                  <div className="space-y-3">
                    {clinic.plans.map((p, i) => (
                      <div key={i} className="p-4 border border-slate-200 rounded-lg" data-testid={`vitrine-plan-${i}`}>
                        <div className="flex items-baseline justify-between gap-2 flex-wrap">
                          <h4 className="font-display font-semibold text-[#0B2B62]">
                            Plano {i + 1} · {p.name}
                          </h4>
                          {p.monthly_price != null && (
                            <span className="text-sm font-mono text-slate-700">
                              R$ {Number(p.monthly_price).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}/mês
                            </span>
                          )}
                        </div>
                        {p.description && <p className="text-xs text-slate-600 mt-1 whitespace-pre-line">{p.description}</p>}
                        {(p.includes || []).length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {p.includes.map((it, j) => (
                              <span key={j} className="text-[11px] bg-slate-100 rounded-full px-2 py-0.5 text-slate-700">{it}</span>
                            ))}
                          </div>
                        )}
                        {(p.room_photos || []).length > 0 && (
                          <div className="mt-3">
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Fotos do quarto</div>
                            <div className="grid grid-cols-4 gap-1.5">
                              {p.room_photos.map((url, k) => (
                                <img key={k} src={url} alt="" className="aspect-square object-cover rounded" />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {(clinic.professionals || []).length > 0 && (
                <section>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Equipe</div>
                  <div className="flex flex-wrap gap-2">
                    {clinic.professionals.map((pr, i) => (
                      <div key={i} className="px-3 py-2 border border-slate-200 rounded-md text-xs">
                        <div className="font-semibold text-slate-800">{pr.name}</div>
                        <div className="text-slate-500">{pr.role}{pr.specialty ? ` · ${pr.specialty}` : ""}</div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
                <b>Confidencial:</b> nome, telefone e endereço da unidade nunca são compartilhados com a família — apenas o código <b className="font-mono">{clinic.code}</b>. A intermediação é 100% do consultor RAF.
              </div>
            </>
          )}
        </div>

        {clinic && (
          <div className="p-4 border-t border-slate-200 flex gap-2 justify-end shrink-0 flex-wrap">
            <button onClick={() => setShowSchedule(true)} data-testid="vitrine-open-schedule"
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 text-slate-700 rounded-md text-sm hover:bg-slate-50">
              <Calendar className="w-4 h-4" /> Agendar visita/vídeo
            </button>
            <button onClick={() => setShowShare(true)} data-testid="vitrine-open-share"
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#0B2B62] text-[#0B2B62] rounded-md text-sm font-semibold hover:bg-blue-50">
              <Send className="w-4 h-4" /> Enviar para família
            </button>
            <button onClick={() => onReserve(clinic.id)} disabled={clinic.beds_available === 0} data-testid="vitrine-reserve"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#0B2B62] text-white rounded-md text-sm font-semibold hover:bg-slate-800 disabled:opacity-40">
              <CheckCircle2 className="w-4 h-4" /> Reservar vaga
            </button>
          </div>
        )}

        {showShare && clinic && (
          <ShareClinicPicker clinic={clinic} caseId={caseData.id}
            onClose={() => setShowShare(false)}
            onDone={() => { setShowShare(false); onClose(); onShared?.(); }} />
        )}
        {showSchedule && clinic && (
          <ScheduleAppointmentPicker clinic={clinic} caseId={caseData.id}
            onClose={() => setShowSchedule(false)}
            onDone={() => { setShowSchedule(false); onClose(); onScheduled?.(); }} />
        )}
      </div>
    </div>
  );
};

/** Modal aninhada: escolher fotos e planos que serão enviados (anônimos). */
const ShareClinicPicker = ({ clinic, caseId, onClose, onDone }) => {
  const [gallery, setGallery] = useState(clinic.gallery || []);
  const [plans, setPlans] = useState((clinic.plans || []).map((p) => ({ ...p, _selected: true })));
  const [mode, setMode] = useState("complete");
  const [sending, setSending] = useState(false);

  const toggleGallery = (url) =>
    setGallery((g) => g.includes(url) ? g.filter((x) => x !== url) : [...g, url]);
  const togglePlan = (i) =>
    setPlans((p) => p.map((x, j) => j === i ? { ...x, _selected: !x._selected } : x));

  const submit = async () => {
    setSending(true);
    try {
      const payload = {
        clinic_id: clinic.id,
        gallery,
        plans: plans.filter((p) => p._selected).map((p, i) => ({
          tier: `Plano ${i + 1} · ${p.name}`,
          monthly_price: p.monthly_price,
          description: p.description,
          includes: p.includes || [],
          room_photos: p.room_photos || [],
        })),
        include_description: true,
        mode,
      };
      await api.post(`/cases/${caseId}/share-clinic`, payload);
      toast.success(`Enviado à família como Clínica ${clinic.code}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao compartilhar");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" data-testid="share-picker-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[88vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Compartilhamento anônimo</div>
            <h3 className="font-display font-bold text-lg text-[#0B2B62]">Enviar Clínica {clinic.code} para família</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 border-b bg-slate-50/50">
          <div className="flex gap-2">
            <button onClick={() => { setMode("complete"); setGallery(clinic.gallery || []); setPlans((clinic.plans || []).map((p) => ({ ...p, _selected: true }))); }}
              data-testid="share-mode-complete"
              className={`text-xs px-3 py-1.5 rounded-full font-semibold ${mode === "complete" ? "bg-[#0B2B62] text-white" : "bg-white border border-slate-300 text-slate-600"}`}>
              Pacote completo
            </button>
            <button onClick={() => setMode("custom")}
              data-testid="share-mode-custom"
              className={`text-xs px-3 py-1.5 rounded-full font-semibold ${mode === "custom" ? "bg-[#0B2B62] text-white" : "bg-white border border-slate-300 text-slate-600"}`}>
              Escolher o que enviar
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {mode === "custom" && (
            <>
              <section>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">
                  Fotos ({gallery.length}/{clinic.gallery?.length || 0})
                </div>
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                  {(clinic.gallery || []).map((url, i) => {
                    const on = gallery.includes(url);
                    return (
                      <button key={i} type="button" onClick={() => toggleGallery(url)} data-testid={`share-photo-${i}`}
                        className={`relative aspect-square rounded-md overflow-hidden border-2 ${on ? "border-[#0B2B62]" : "border-transparent opacity-50"}`}>
                        <img src={url} alt="" className="w-full h-full object-cover" />
                        {on && <div className="absolute top-1 right-1 w-5 h-5 bg-[#0B2B62] text-white rounded-full flex items-center justify-center"><CheckCircle2 className="w-3.5 h-3.5" /></div>}
                      </button>
                    );
                  })}
                </div>
              </section>
              <section>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Planos</div>
                <div className="space-y-2">
                  {plans.map((p, i) => (
                    <label key={i} data-testid={`share-plan-${i}`}
                      className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer ${p._selected ? "border-[#0B2B62] bg-blue-50/40" : "border-slate-200"}`}>
                      <input type="checkbox" checked={!!p._selected} onChange={() => togglePlan(i)} className="mt-1" />
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm text-[#0B2B62]">Plano {i + 1} · {p.name}</div>
                        {p.monthly_price != null && <div className="text-xs text-slate-500">R$ {Number(p.monthly_price).toLocaleString("pt-BR")}/mês</div>}
                      </div>
                    </label>
                  ))}
                </div>
              </section>
            </>
          )}
          {mode === "complete" && (
            <div className="p-4 rounded-md bg-blue-50 border border-blue-200 text-xs text-slate-700 space-y-1">
              <div className="font-semibold text-[#0B2B62]">Será enviado:</div>
              <div>· {clinic.gallery?.length || 0} foto(s) da unidade</div>
              <div>· {(clinic.plans || []).length} plano(s) com fotos dos quartos</div>
              <div>· Descrição da unidade (sem nome/contato)</div>
              <div>· Identificada como <b className="font-mono">Clínica {clinic.code} · {clinic.city}, {clinic.state}</b></div>
            </div>
          )}
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md">Cancelar</button>
          <button onClick={submit} disabled={sending} data-testid="share-submit"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0B2B62] text-white rounded-md text-sm font-semibold hover:bg-slate-800 disabled:opacity-50">
            {sending && <Loader2 className="w-4 h-4 animate-spin" />}
            <Send className="w-4 h-4" /> Enviar
          </button>
        </div>
      </div>
    </div>
  );
};

const ScheduleAppointmentPicker = ({ clinic, caseId, onClose, onDone }) => {
  const [type, setType] = useState("visita");
  const [when, setWhen] = useState("");
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);
  const submit = async () => {
    if (!when) { toast.error("Informe data e hora"); return; }
    setSending(true);
    try {
      await api.post(`/cases/${caseId}/appointment`, {
        clinic_id: clinic.id, type, when, notes,
      });
      toast.success(`${type === "visita" ? "Visita" : "Videochamada"} agendada com Clínica ${clinic.code}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao agendar");
    } finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" data-testid="schedule-modal">
      <div className="bg-white rounded-xl max-w-md w-full">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Agendamento</div>
            <h3 className="font-display font-bold text-lg text-[#0B2B62]">Clínica {clinic.code}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Tipo</label>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setType("visita")} data-testid="sched-type-visita"
                className={`px-3 py-2 rounded-md border text-sm font-semibold ${type === "visita" ? "border-[#0B2B62] bg-[#0B2B62] text-white" : "border-slate-300 text-slate-700"}`}>
                Visita presencial
              </button>
              <button type="button" onClick={() => setType("videochamada")} data-testid="sched-type-video"
                className={`px-3 py-2 rounded-md border text-sm font-semibold ${type === "videochamada" ? "border-[#0B2B62] bg-[#0B2B62] text-white" : "border-slate-300 text-slate-700"}`}>
                Videochamada
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Data e hora</label>
            <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
              data-testid="sched-when"
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Observações (opcional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              data-testid="sched-notes"
              placeholder="Ex: apresentar acompanhante, discutir o plano 2, etc."
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md text-sm resize-none" />
          </div>
          <div className="p-3 rounded-md bg-slate-50 text-[11px] text-slate-600">
            A família verá o compromisso identificado apenas como <b className="font-mono">Clínica {clinic.code}</b>. Endereço/link será enviado pelo consultor à parte.
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md">Cancelar</button>
          <button onClick={submit} disabled={sending} data-testid="sched-submit"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0B2B62] text-white rounded-md text-sm font-semibold hover:bg-slate-800 disabled:opacity-50">
            {sending && <Loader2 className="w-4 h-4 animate-spin" />}
            <Calendar className="w-4 h-4" /> Confirmar
          </button>
        </div>
      </div>
    </div>
  );
};
